import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import {
  LIFESTYLE_PRESETS,
  type ComparisonConfig,
  type ComparisonExtra,
  type FaqItem,
  type LifestyleBlock,
  EMPTY_COMPARISON_CONFIG,
} from "@/lib/listingPhase3";

export interface Phase3Value {
  lifestyle: LifestyleBlock[];
  faqs: FaqItem[];
  comparison_config: ComparisonConfig;
}

export const EMPTY_PHASE3: Phase3Value = {
  lifestyle: [],
  faqs: [],
  comparison_config: EMPTY_COMPARISON_CONFIG,
};

interface Props {
  value: Phase3Value;
  onChange: (next: Phase3Value) => void;
}

const Phase3Form = ({ value, onChange }: Props) => {
  const patch = (p: Partial<Phase3Value>) => onChange({ ...value, ...p });

  // ── Lifestyle helpers ──
  const addLifestyle = (preset?: LifestyleBlock) => {
    const item = preset || { icon: "✨", title: "Novo bloco", description: "" };
    patch({ lifestyle: [...value.lifestyle, item] });
  };
  const updateLifestyle = (i: number, next: Partial<LifestyleBlock>) => {
    const list = value.lifestyle.slice();
    list[i] = { ...list[i], ...next };
    patch({ lifestyle: list });
  };
  const removeLifestyle = (i: number) =>
    patch({ lifestyle: value.lifestyle.filter((_, idx) => idx !== i) });
  const moveLifestyle = (i: number, dir: -1 | 1) => {
    const list = value.lifestyle.slice();
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    patch({ lifestyle: list });
  };

  // ── FAQ helpers ──
  const addFaq = () =>
    patch({ faqs: [...value.faqs, { question: "", answer: "" }] });
  const updateFaq = (i: number, next: Partial<FaqItem>) => {
    const list = value.faqs.slice();
    list[i] = { ...list[i], ...next };
    patch({ faqs: list });
  };
  const removeFaq = (i: number) =>
    patch({ faqs: value.faqs.filter((_, idx) => idx !== i) });
  const moveFaq = (i: number, dir: -1 | 1) => {
    const list = value.faqs.slice();
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    patch({ faqs: list });
  };

  // ── Comparison helpers ──
  const cc = value.comparison_config || EMPTY_COMPARISON_CONFIG;
  const patchCC = (p: Partial<ComparisonConfig>) =>
    patch({ comparison_config: { ...cc, ...p } });
  const addExtra = () =>
    patchCC({ extras: [...(cc.extras || []), { label: "", value: "" }] });
  const updateExtra = (i: number, next: Partial<ComparisonExtra>) => {
    const list = (cc.extras || []).slice();
    list[i] = { ...list[i], ...next };
    patchCC({ extras: list });
  };
  const removeExtra = (i: number) =>
    patchCC({ extras: (cc.extras || []).filter((_, idx) => idx !== i) });

  return (
    <details className="border border-border rounded-md">
      <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
        🌟 Lifestyle, FAQ e comparativo automático (opcional)
      </summary>
      <div className="p-3 space-y-5">
        {/* ── Lifestyle ── */}
        <div>
          <p className="text-xs font-semibold text-foreground font-body mb-2">
            Seção "Viva no imóvel" — blocos de estilo de vida
          </p>
          <div className="flex flex-wrap gap-1 mb-2">
            {LIFESTYLE_PRESETS.map((p) => (
              <button
                key={p.title}
                type="button"
                onClick={() => addLifestyle(p)}
                className="text-xs font-body px-2 py-0.5 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {p.icon} {p.title}
              </button>
            ))}
            <button
              type="button"
              onClick={() => addLifestyle()}
              className="text-xs font-body px-2 py-0.5 rounded-full border border-dashed border-border hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" /> personalizado
            </button>
          </div>
          <div className="space-y-2">
            {value.lifestyle.map((b, i) => (
              <div key={i} className="border border-border rounded-md p-2 bg-muted/30 space-y-1">
                <div className="flex items-center gap-1">
                  <Input
                    value={b.icon}
                    onChange={(e) => updateLifestyle(i, { icon: e.target.value })}
                    placeholder="Ícone (emoji)"
                    className="font-body w-20 text-center"
                    maxLength={4}
                  />
                  <Input
                    value={b.title}
                    onChange={(e) => updateLifestyle(i, { title: e.target.value })}
                    placeholder="Título"
                    className="font-body font-semibold text-sm"
                    maxLength={60}
                  />
                  <Button type="button" size="icon" variant="ghost" onClick={() => moveLifestyle(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => moveLifestyle(i, 1)} disabled={i === value.lifestyle.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeLifestyle(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <Textarea
                  value={b.description}
                  onChange={(e) => updateLifestyle(i, { description: e.target.value })}
                  placeholder="Descrição curta"
                  rows={2}
                  className="font-body"
                />
              </div>
            ))}
            {value.lifestyle.length === 0 && (
              <p className="text-xs text-muted-foreground font-body">Nenhum bloco. Clique nos presets acima ou em "personalizado".</p>
            )}
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-foreground font-body">Perguntas frequentes (FAQ)</p>
            <Button type="button" size="sm" variant="outline" onClick={addFaq}>
              <Plus className="h-3 w-3 mr-1" /> Nova pergunta
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground font-body mb-2">Aparece na página do imóvel e gera FAQPage Schema.org automaticamente.</p>
          <div className="space-y-2">
            {value.faqs.map((f, i) => (
              <div key={i} className="border border-border rounded-md p-2 bg-muted/30 space-y-1">
                <div className="flex items-center gap-1">
                  <Input
                    value={f.question}
                    onChange={(e) => updateFaq(i, { question: e.target.value })}
                    placeholder="Pergunta"
                    className="font-body font-semibold text-sm"
                  />
                  <Button type="button" size="icon" variant="ghost" onClick={() => moveFaq(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => moveFaq(i, 1)} disabled={i === value.faqs.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeFaq(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <Textarea
                  value={f.answer}
                  onChange={(e) => updateFaq(i, { answer: e.target.value })}
                  placeholder="Resposta"
                  rows={2}
                  className="font-body"
                />
              </div>
            ))}
            {value.faqs.length === 0 && (
              <p className="text-xs text-muted-foreground font-body">Nenhuma pergunta cadastrada.</p>
            )}
          </div>
        </div>

        {/* ── Comparison ── */}
        <div className="border-t border-border pt-3">
          <label className="flex items-center gap-2 mb-2 font-body text-sm">
            <input
              type="checkbox"
              checked={!!cc.enabled}
              onChange={(e) => patchCC({ enabled: e.target.checked })}
            />
            <span className="font-semibold">Ativar comparativo automático</span>
          </label>
          <p className="text-[10px] text-muted-foreground font-body mb-2">
            Mostra uma tabela comparando este imóvel com os "Imóveis Semelhantes" selecionados acima.
          </p>
          {cc.enabled && (
            <div className="space-y-2">
              <Input
                value={cc.highlight || ""}
                onChange={(e) => patchCC({ highlight: e.target.value })}
                placeholder='Destaque deste imóvel (ex.: "Melhor custo-benefício")'
                className="font-body"
              />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-foreground font-body">Critérios adicionais (opcional)</p>
                  <Button type="button" size="sm" variant="outline" onClick={addExtra}>
                    <Plus className="h-3 w-3 mr-1" /> Novo critério
                  </Button>
                </div>
                <div className="space-y-1">
                  {(cc.extras || []).map((x, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <Input
                        value={x.label}
                        onChange={(e) => updateExtra(i, { label: e.target.value })}
                        placeholder="Rótulo (ex.: Vista para o mar)"
                        className="font-body"
                      />
                      <Input
                        value={x.value}
                        onChange={(e) => updateExtra(i, { value: e.target.value })}
                        placeholder="Valor deste imóvel (ex.: Sim)"
                        className="font-body"
                      />
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeExtra(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </details>
  );
};

export default Phase3Form;
